import { create } from "zustand";
import axios from "axios";
import { BASE_URL } from "../..";
import {
  ADD_CART_ITEM_API,
  CART_REMOVE_ITEM_API,
  GET_CART_API,
  POST_CART_DETAILS_API,
} from "../../app/constants";

const useCartStore = create((set, get) => ({
  cartArray: [],
  cartDetails: [],
  isCheckedAll: false,
  loading: true,
  showModal: false, // Add this to the state
  charges: {},
  selectedAll: [],
  data: [],
  deliveryMessage: null,
  setDeliveryMessage: (newDeliveryMessage) =>
    set({ deliveryMessage: newDeliveryMessage }),
  fetchCartItems: async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get(`${BASE_URL}${GET_CART_API}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 && response.data.code === 1) {
        const cartItems = response.data.data.cartItems.map((item) => ({
          id: item.cartItemId,
          inventoryId: item.inventory.id, // Ensure inventoryId is included
          productId: item.cartItemId,
          slug: item.inventory.productDto.slug,
          productName: item.inventory.productDto.title,
          productImg: item.inventory.productDto.image,
          productPrice: item.inventory.productDto.offered,
          selling: item.inventory.productDto.selling,
          quantity: item.quantity,
          description:
            item.inventory.productDto.description || "No description available",
          isChecked:
            item.cartItemStatus === "CHECKED_OUT"
              ? true
              : item.cartItemStatus === "IN_CART"
              ? false
              : item.isChecked,
        }));

        set({ cartArray: cartItems });
        return cartItems;
      } else {
        console.log(response.data.message);
        return [];
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
      return [];
    }
  },

  handleCheckboxChange: (id) => {
    const { cartArray, selectedAll, fetchCartDetails } = get();

    const updatedCart = cartArray.map((item) =>
      item.id === id ? { ...item, isChecked: !item.isChecked } : item
    );

    const updatedItem = updatedCart.find((item) => item.id === id);
    const updatedSelectedAll = updatedItem.isChecked
      ? [...selectedAll, id]
      : selectedAll.filter((itemId) => itemId !== id);

    set({
      cartArray: updatedCart,
      selectedAll: updatedSelectedAll,
      isCheckedAll: updatedCart.length === updatedSelectedAll.length,
    });

    const updatedData = updatedCart.map((item) => ({
      id: item.id,
      checked: item.isChecked,
    }));

    fetchCartDetails(updatedData);
  },

  handleIsCheckedAll: () => {
    const { cartArray, fetchCartDetails } = get();

    const newCheckedStatus = !get().isCheckedAll;
    const updatedCart = cartArray.map((item) => ({
      ...item,
      isChecked: newCheckedStatus,
    }));

    set({
      cartArray: updatedCart,
      isCheckedAll: newCheckedStatus,
      selectedAll: newCheckedStatus ? updatedCart.map((item) => item.id) : [],
    });

    const updatedData = updatedCart.map((item) => ({
      id: item.id,
      checked: newCheckedStatus,
    }));

    fetchCartDetails(updatedData);
  },

  fetchCartDetails: async (cartData) => {
    const token = localStorage.getItem("accessToken");

    try {
      const response = await axios.post(
        `${BASE_URL}${GET_CART_API}${POST_CART_DETAILS_API}`,
        cartData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 && response.data.code === 1) {
        const { cartItems, charges } = response.data.data;

        if (cartItems) {
          const mappedCartItems = cartItems.map((item) => ({
            id: item.cartItemId,
            inventoryId: item.inventory.id,
            productName: item.inventory.productDto.title,
            productImg: item.inventory.productDto.image,
            productPrice: item.inventory.productDto.selling,
            productOffered: item.inventory.productDto.offered,
            quantity: item.quantity,
            description:
              item.inventory.productDto.description ||
              "No description available",
            // isChecked: false,
            isChecked:
              item.cartItemStatus === "CHECKED_OUT"
                ? true
                : item.cartItemStatus === "IN_CART"
                ? false
                : item.isChecked,
          }));
          set({ cartDetails: mappedCartItems });
        } else {
          set({ cartDetails: [] });
        }

        // Correctly call setDeliveryMessage from the store
        const deliveryMessage = response.data.data.deliveryMessage;
        set({ deliveryMessage });

        // Update charges
        set({ charges });
      } else {
        console.log(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching cart details:", error);
    }
  },

  handleRemoveFromCart: async (id) => {
    const token = localStorage.getItem("accessToken");

    try {
      const response = await axios.delete(
        `${BASE_URL}${GET_CART_API}${CART_REMOVE_ITEM_API}/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 && response.data.code === 1) {
        set((state) => ({
          cartArray: state.cartArray.filter((item) => item.id !== id),
        }));
        console.log("Product removed from cart!");
      } else {
        console.log("Failed to remove product from cart.");
      }
    } catch (error) {
      console.error("Error removing product from cart:", error);
    }
  },

  handleRemoveAllFromCart: async () => {
    const token = localStorage.getItem("accessToken");
    const { selectedAll } = get();

    try {
      const deletePromises = selectedAll.map((id) =>
        axios.delete(
          `${BASE_URL}${GET_CART_API}${CART_REMOVE_ITEM_API}/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
      );

      const responses = await Promise.all(deletePromises);
      const allSuccess = responses.every(
        (response) => response.status === 200 && response.data.code === 1
      );

      if (allSuccess) {
        set((state) => ({
          cartArray: state.cartArray.filter(
            (item) => !selectedAll.includes(item.id)
          ),
          selectedAll: [],
        }));
        console.log("Selected products removed from cart!");
      } else {
        console.log("Failed to remove some products from cart.");
      }
    } catch (error) {
      console.error("Error removing products from cart:", error);
    }
  },

  handleCloseModal: () => set({ showModal: false }),
  handleShowModal: () => {
    const { cartArray } = get();
    const selectedItems = cartArray.filter((item) => item.isChecked);

    if (selectedItems.length > 0) {
      set({ showModal: true });
    } else {
      console.log("Please select a product to delete.");
    }
  },

  handleIsCheckedDel: () => {
    const { cartArray } = get();
    const selectedItems = cartArray.filter((item) => item.isChecked);

    if (selectedItems.length === 0) {
      console.log("Please select a product you want to delete.");
    } else {
      set((state) => ({
        cartArray: state.cartArray.filter((item) => !item.isChecked),
        showModal: false,
      }));
    }
  },

  postUpdatedQuantity: async (inventoryId, quantity) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.post(
        `${BASE_URL}${ADD_CART_ITEM_API}`,
        { productInventoryId: inventoryId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200 && response.data.code === 1) {
        console.log("Quantity updated successfully:", {
          inventoryId,
          quantity,
        });
      } else {
        console.log("Failed to update the quantity.");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  },

  incrementQuantity: (id) => {
    const { cartArray } = get();
    const updatedCart = cartArray.map((item) => {
      if (item.id === id) {
        const updatedQuantity = item.quantity + 1;
        get().postUpdatedQuantity(item.inventoryId, updatedQuantity); // Use correct inventoryId
        return { ...item, quantity: updatedQuantity };
      }
      return item;
    });
    set({ cartArray: updatedCart });
  },

  decrementQuantity: (id) => {
    const { cartArray } = get();
    const updatedCart = cartArray.map((item) => {
      if (item.id === id && item.quantity > 1) {
        const updatedQuantity = item.quantity - 1;
        get().postUpdatedQuantity(item.inventoryId, updatedQuantity); // Use correct inventoryId
        return { ...item, quantity: updatedQuantity };
      }
      return item;
    });
    set({ cartArray: updatedCart });
  },
}));

export default useCartStore;
